import { BoardsV2AlphaHold } from '@run-diffusion/shared';
import { EMAIL_REGEX } from '@/constants/constants';

/**
 * The Boards v2 alpha's CSV (IL-107): the emails read from a file, and the
 * review rows the Boards v2 Rollout page shows once each one has been checked
 * against RunDiffusion's accounts (the lookupBoardsV2AlphaEmails callable,
 * firebase/functions/src/useCases/boardsV2Alpha.ts). A reviewed list can be
 * held to add later instead (IL-114): its emails are kept in
 * boardsV2AlphaHolds, and checked again when it is opened.
 */

// The callables take this many emails or people a call (BOARDS_V2_ALPHA_BATCH)
export const ALPHA_BATCH: number = 200;
// A file with more is refused: split it
export const ALPHA_MAX_EMAILS: number = 2000;
export const ALPHA_MAX_FILE_BYTES: number = 5 * 1024 * 1024;

// The lookup's answer, as firebase/functions/src/types/onCallData.ts has it
export interface BoardsV2AlphaTeam {
	id: string
	name: string
	companyId: string | null
	companyName: string | null
	boardsV2: boolean
}
export interface BoardsV2AlphaMatch {
	email: string
	userId: string | null
	deleted: boolean
	isAdmin: boolean
	inAlpha: boolean
	teams: BoardsV2AlphaTeam[]
}

export interface CsvEmails {
	// trimmed, in lower case, each once, in the file's order
	emails: string[]
	// cells with an @ that aren't an email
	invalid: string[]
	// emails seen again, dropped
	repeats: number
}

/**
 * Every email in a CSV, or any text. Each cell is read whatever its column,
 * so a header row, names and other columns are passed over, and
 * "Ana Ruiz <Ana@Studio.com>" gives ana@studio.com.
 */
export function readCsvEmails (text: string): CsvEmails {
	const seen: Set<string> = new Set();
	const out: CsvEmails = { emails: [], invalid: [], repeats: 0 };
	text.split(/[\r\n,;\t]+/).forEach((cell: string) => {
		const raw: string = cell.replace(/"/g, '').trim();
		if (!raw.includes('@')) return;
		const found: string[] = raw.match(/[^\s<>()[\]]+@[^\s<>()[\]]+/g) || [raw];
		found.forEach((token: string) => {
			const email: string = token.replace(/^mailto:/i, '').replace(/\.+$/, '').toLowerCase();
			if (seen.has(email)) {
				out.repeats++;
				return;
			}
			seen.add(email);
			(EMAIL_REGEX.test(email) ? out.emails : out.invalid).push(email);
		});
	});
	return out;
}

export type AlphaRowStatus =
	| 'checking'
	| 'error' // the check failed: check again
	| 'ready' // an account, not in yet
	| 'in-alpha' // in already
	| 'admin' // a RunDiffusion admin, who has Boards v2 anyway
	| 'added' // added from this review
	| 'missing' // no account has the email
	| 'deleted' // the account was deleted
	| 'invalid'; // not an email

export interface AlphaReviewRow {
	email: string
	status: AlphaRowStatus
	userId: string | null
	teams: BoardsV2AlphaTeam[]
}

export type AlphaRowGroup = 'ready' | 'has-it' | 'not-found' | 'pending';

export function alphaRowGroup (status: AlphaRowStatus): AlphaRowGroup {
	if (status === 'ready') return 'ready';
	if (status === 'in-alpha' || status === 'admin' || status === 'added') return 'has-it';
	if (status === 'missing' || status === 'deleted' || status === 'invalid') return 'not-found';
	return 'pending';
}

export function rowFromMatch (match: BoardsV2AlphaMatch): AlphaReviewRow {
	let status: AlphaRowStatus = 'ready';
	if (!match.userId) status = 'missing';
	else if (match.deleted) status = 'deleted';
	else if (match.inAlpha) status = 'in-alpha';
	else if (match.isAdmin) status = 'admin';
	return { email: match.email, status, userId: match.userId, teams: match.teams || [] };
}

export interface AlphaMembership {
	company: string | null
	// the account's teams in that company, or one team outside a company
	teams: string
}

/** Where an account works: each company with its teams, then teams outside a company. */
export function groupMemberships (teams: Array<Pick<BoardsV2AlphaTeam, 'name' | 'companyId' | 'companyName'>>): AlphaMembership[] {
	const byCompany: Map<string, { company: string, teams: string[] }> = new Map();
	const loose: AlphaMembership[] = [];
	teams.forEach((team) => {
		const teamName: string = team.name || '(no name)';
		if (!team.companyId) {
			loose.push({ company: null, teams: teamName });
			return;
		}
		const entry = byCompany.get(team.companyId) || { company: team.companyName || '(no name)', teams: [] };
		entry.teams.push(teamName);
		byCompany.set(team.companyId, entry);
	});
	return [
		...Array.from(byCompany.values()).map((c) => ({ company: c.company, teams: c.teams.join(', ') })),
		...loose,
	];
}

/** The same in a line: "Studio Co: Design, Render · Freelancers" */
export function membershipLabel (teams: Array<Pick<BoardsV2AlphaTeam, 'name' | 'companyId' | 'companyName'>>): string {
	return groupMemberships(teams).map((m: AlphaMembership) => (m.company ? `${m.company}: ${m.teams}` : m.teams)).join(' · ');
}

/** "3 not in the system and 2 already in are left out." */
export function leftOutNote (parts: Array<{ count: number, label: string }>): string {
	const shown = parts.filter((p) => p.count > 0);
	if (!shown.length) return '';
	const total: number = shown.reduce((n: number, p) => n + p.count, 0);
	const phrases: string[] = shown.map((p) => `${p.count} ${p.label}`);
	const list: string = phrases.length > 1 ? `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}` : phrases[0];
	return `${list} ${total === 1 ? 'is' : 'are'} left out.`;
}

// Where held lists are kept (BoardsV2AlphaHold); admins alone may read or write them
export const ALPHA_HOLDS: string = 'boardsV2AlphaHolds';

// A held list, as the page shows it
export interface AlphaHold {
	id: string
	fileName: string
	emails: string[]
	ready: number
	heldByEmail: string
	// when it was held last
	heldAt: Date | null
}

// What the page writes; the db middleware stamps _created and _updated
export type AlphaHoldData = Pick<BoardsV2AlphaHold, 'fileName' | 'emails' | 'ready' | 'heldBy' | 'heldByEmail'>;
export type AlphaHoldDoc = AlphaHoldData & Partial<Pick<BoardsV2AlphaHold, '_updated'>>;

/** What holding a review keeps: its rows as they stand, and who held them. */
export function holdData (rows: AlphaReviewRow[], fileName: string, by: { id: string, email: string }): AlphaHoldData {
	return {
		fileName,
		emails: rows.map((row: AlphaReviewRow) => row.email),
		ready: rows.filter((row: AlphaReviewRow) => row.status === 'ready').length,
		heldBy: by.id,
		heldByEmail: by.email,
	};
}

/** A held list's doc as the page shows it. Read with the server's estimate, a time being written isn't missing. */
export function holdFromDoc (id: string, data: AlphaHoldDoc): AlphaHold {
	return {
		id,
		fileName: data.fileName,
		emails: data.emails,
		ready: data.ready,
		heldByEmail: data.heldByEmail,
		heldAt: data._updated ? data._updated.toDate() : null,
	};
}

/** The last held first */
export function newestFirst (a: AlphaHold, b: AlphaHold): number {
	const time = (hold: AlphaHold): number => (hold.heldAt ? hold.heldAt.getTime() : Number.MAX_SAFE_INTEGER);
	return time(b) - time(a);
}

/** A held list's emails, read back as a file's are: the ones to check, and the cells that aren't emails. */
export function holdEmails (hold: Pick<AlphaHold, 'emails'>): CsvEmails {
	return readCsvEmails(hold.emails.join('\n'));
}

/** "42 emails, 38 ready when held" */
export function holdSummary (hold: Pick<AlphaHold, 'emails' | 'ready'>): string {
	const n: number = hold.emails.length;
	return `${n} ${n === 1 ? 'email' : 'emails'}, ${hold.ready} ready when held`;
}

const MONTHS: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** "today at 4:12 PM", "yesterday at 9:05 AM", "Nov 2 at 4:12 PM", or "Dec 30, 2025 at 4:12 PM", in local time */
export function heldAtLabel (at: Date, now: Date): string {
	const hours: number = at.getHours();
	const time: string = `${hours % 12 || 12}:${String(at.getMinutes()).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
	// rounded, so a day an hour short or long (daylight saving) is still a day
	const days: number = Math.round((startOfDay(now) - startOfDay(at)) / 86400000);
	if (days <= 0) return `today at ${time}`;
	if (days === 1) return `yesterday at ${time}`;
	const date: string = `${MONTHS[at.getMonth()]} ${at.getDate()}`;
	return `${at.getFullYear() === now.getFullYear() ? date : `${date}, ${at.getFullYear()}`} at ${time}`;
}

/** "Held by ana@studio.com yesterday at 4:12 PM" */
export function heldByLabel (hold: Pick<AlphaHold, 'heldByEmail' | 'heldAt'>, now: Date): string {
	const by: string = hold.heldByEmail ? `Held by ${hold.heldByEmail}` : 'Held';
	return `${by} ${hold.heldAt ? heldAtLabel(hold.heldAt, now) : 'just now'}`;
}
